<script>
import axios from "axios";
import API from "@/services/API";
import Button2 from "@/components/ui/Button2.vue";
import { checkValidation } from "@/components/watchmen/check-validation.js";
import { CHANNEL_TYPE } from "./enum-channel-type"

const UPDATE = Object.freeze({
  ATTEMPTS: 45,
  DURATION: 4000,
  FIRST_DURATION: 8000,
});

export const VALIDATION = Object.freeze({
  NONE: "NONE",
  WAITING: "WAITING",
  VALIDATED: "VALIDATED",
  NO_ANSWER: "NO_ANSWER",
  FAILED: "FAILED",
  RECALL: "RECALL",
  SENT: "SENT",
  EXPIRED: "EXPIRED",
});
export const VALIDATE_STATE_API = Object.freeze({
  NONE: null,
  WAITING: "waiting",
  VALIDATED: "valid",
  NO_ANSWER: "unanswered",
  FAILED: "failed",
  INVALID: "invalid",
  SENT: "sent",
  EXPIRED: "expired",
});

export default {
  name: "ChannelValidation",
  components: { Button2 },
  props: {
    channel: {
      type: Object,
      required: true,
    },
  },
  data: function () {
    return {
      VALIDATION,
      VALIDATE_STATE_API,
      UPDATE,
      CHANNEL_TYPE,
      validationStatus: this.checkValidation(this.channel)[0]
        ? VALIDATION.VALIDATED
        : this.validationSwitch(this.checkValidation(this.channel)[1]),
    };
  },
  methods: {
    checkValidation: checkValidation,
    validationSwitch: function (validation_status) {
      switch (validation_status) {
        case VALIDATE_STATE_API.NONE:
          return VALIDATION.NONE;
        case VALIDATE_STATE_API.WAITING:
          return VALIDATION.WAITING;
        case VALIDATE_STATE_API.VALIDATED:
          return VALIDATION.VALIDATED;
        case VALIDATE_STATE_API.NO_ANSWER:
          return VALIDATION.NO_ANSWER;
        case VALIDATE_STATE_API.FAILED:
        case VALIDATE_STATE_API.INVALID:
          return VALIDATION.FAILED;
        case VALIDATE_STATE_API.SENT:
          return VALIDATION.SENT;
        case VALIDATE_STATE_API.EXPIRED:
          return VALIDATION.EXPIRED;
      }
    },
    callForValidation: function () {
      this.validationStatus = VALIDATION.WAITING;

      axios
        .post(
          API.route(API.endpoints.channels.validate, {
            channelId: this.channel.id,
          })
        )
        .then(() => {})
        .catch((error) => {
          console.log(error.data?.message);
        });

      setTimeout(() => {
        this.getValidationUpdate(UPDATE.ATTEMPTS, UPDATE.DURATION);
      }, UPDATE.FIRST_DURATION);
    },
    getValidationUpdate: function (attempts, duration) {
  
      axios
        .get(
          API.route(API.endpoints.channels.show, { channelId: this.channel.id })
        )
        .then((response) => {
          if (response.data) {
            this.$emit("onChannelFetched", response.data);
            this.validationStatus = this.checkValidation(response.data)[0]
              ? VALIDATION.VALIDATED
              : this.validationSwitch(this.checkValidation(response.data)[1]);
          }
        })
        .catch((error) => {
          console.log(error);
        });

      if (
        ( attempts > 0 && this.validationStatus === VALIDATION.WAITING ) ||
        ( attempts > 0 && this.validationStatus === VALIDATION.SENT )
      ) {
        setTimeout(() => {
          this.getValidationUpdate(attempts - 1, duration);
        }, duration);
      } 
    },
  },
  watch: {
    channel: function (newChannel, oldChannel) {
      this.validationStatus = this.checkValidation(newChannel)[0]
        ? VALIDATION.VALIDATED
        : this.validationSwitch(this.checkValidation(newChannel)[1]);
    },
  },
};
</script>

<template>
  <div>
    <div v-if="validationStatus === VALIDATION.NONE">
      <Button2
        @click.native.prevent="callForValidation()"
        class="btn-sm btn-info"
      >
        <span v-if="this.channel.type !== CHANNEL_TYPE.EMAIL">
          {{ $t("watchmen.validation.validate") }}
        </span>
        <span v-if="this.channel.type === CHANNEL_TYPE.EMAIL">
          {{ $t("watchmen.validation.validate_email") }}
        </span>
      </Button2>
    </div>
    <div v-if="validationStatus === VALIDATION.WAITING">
      <span class="d-inline-flex align-items-center text-secondary">
        <span v-if="this.channel.type === CHANNEL_TYPE.PHONE" class="spinner-grow spinner-grow-sm mr-2" role="status"></span>
        <span v-if="this.channel.type === CHANNEL_TYPE.PHONE">
          {{ $t("watchmen.validation.waiting") }}
        </span>
        <span v-if="this.channel.type === CHANNEL_TYPE.MOBILE">
          {{ $t("watchmen.validation.waiting_mobile") }}
        </span>
        <span v-if="this.channel.type === CHANNEL_TYPE.EMAIL">
          {{ $t("watchmen.validation.waiting_email") }}
        </span>
      </span>
    </div>
    <div v-if="validationStatus === VALIDATION.VALIDATED">
      <span class="badge-success p-1 px-2">
        <i class="fa fa-check mr-1"></i>
        {{ $t("watchmen.validation.validated") }}
      </span>
    </div>
    <div v-if="validationStatus === VALIDATION.NO_ANSWER">
      <span class="d-inline-flex align-items-center text-secondary">
        <span class="text-danger">
          <i class="fa fa-exclamation-triangle mr-1" aria-hidden="true"></i>
          {{ $t("watchmen.validation.no_answer") }}
        </span>
        <Button2
          @click.native.prevent="callForValidation()"
          class="btn-sm btn-info ml-2"
        >
          <i class="fa fa-repeat" aria-hidden="true"></i>
          <span class="sr-only">{{ $t("watchmen.validation.retry") }}</span>
        </Button2>
      </span>
    </div>
    <div v-if="validationStatus === VALIDATION.FAILED">
      <span class="d-inline-flex align-items-center text-secondary">
        <span class="text-danger">
          <i class="fa fa-exclamation-triangle mr-1" aria-hidden="true"></i>
          {{ $t("watchmen.validation.failed") }}
        </span>
        <Button2
          @click.native.prevent="callForValidation()"
          class="btn-sm btn-info ml-2"
        >
          <i class="fa fa-repeat" aria-hidden="true"></i>
          <span class="sr-only">{{ $t("watchmen.validation.retry") }}</span>
        </Button2>
      </span>
    </div>
    <div v-if="validationStatus === VALIDATION.SENT">
      <span v-if="this.channel.type === CHANNEL_TYPE.MOBILE" class="text-secondary">
        {{ $t("watchmen.validation.sent_mobile") }}
      </span>
      <span v-if="this.channel.type === CHANNEL_TYPE.EMAIL" class="text-secondary">
        {{ $t("watchmen.validation.sent_email") }}
      </span>
    </div>
    <div v-if="validationStatus === VALIDATION.EXPIRED">
      <span class="d-inline-flex align-items-center text-secondary">
        <span class="text-danger">
          <i class="fa fa-exclamation-triangle mr-1" aria-hidden="true"></i>
          {{ $t("watchmen.validation.expired") }}
        </span>
        <Button2
          @click.native.prevent="callForValidation()"
          class="btn-sm btn-info ml-2"
        >
          <i class="fa fa-repeat" aria-hidden="true"></i>
          <span class="sr-only">{{ $t("watchmen.validation.retry") }}</span>
        </Button2>
      </span>
    </div>
  </div>
</template>
